import { useState, useEffect } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { tryResolveTenantId } from '../utils/tenant';

export function useAvailability(locationId = null, employeeId = null) {
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If neither locationId nor employeeId provided, nothing to listen to
    if (!locationId && !employeeId) {
      setAvailability([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let unsubscribe = null;

    (async () => {
      setLoading(true);
      // Prefer tenantId stored in localStorage (adminUser) for quicker resolution in admin UI
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId) {
        tenantId = await tryResolveTenantId();
      }
      if (cancelled) return;
      if (!tenantId) {
        setAvailability([]);
        setLoading(false);
        return;
      }

      try {
        let base = collection(db, `tenants/${tenantId}/availability`);
        let q = null;

        // Build query with available filters
        if (locationId && employeeId) {
          q = query(base, where("locationId", "==", locationId), where("employeeId", "==", employeeId));
        } else if (locationId) {
          q = query(base, where("locationId", "==", locationId));
        } else if (employeeId) {
          q = query(base, where("employeeId", "==", employeeId));
        }

        if (!q) {
          setAvailability([]);
          setLoading(false);
          return;
        }

        unsubscribe = onSnapshot(q, (snapshot) => {
          try {
            if (cancelled) return;
            const availabilityData = [];
            snapshot.forEach((doc) => {
              availabilityData.push({ id: doc.id, ...doc.data() });
            });
            setAvailability(availabilityData);
            setLoading(false);
          } catch (procErr) {
            // Log processing error but don't throw to avoid bubbling into Firestore internals
            // Include context to help debugging
            console.error('Error processing availability snapshot', procErr, { tenantId, locationId, employeeId });
            if (!cancelled) {
              setAvailability([]);
              setLoading(false);
            }
          }
        }, (err) => {
          console.warn('availability listener error', err, { tenantId, locationId, employeeId });
          if (cancelled) return;
          setAvailability([]);
          setLoading(false);
        });
      } catch (e) {
        console.warn('failed to attach availability listener', e);
        if (!cancelled) {
          setAvailability([]);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { if (typeof unsubscribe === 'function') unsubscribe(); } catch (e) { /* ignore */ }
    };
  }, [locationId, employeeId]);

  return { availability, loading };
}

// Helper function to check if an employee is available on a specific day and time
export function isEmployeeAvailable(availability, employeeId, dayOfWeek, startTime, endTime, referenceDate = null) {
  // Find the most recent applicable availability record for this employee
  // Use the provided referenceDate (shift date) when evaluating temporary availability
  const ref = referenceDate ? (referenceDate.toDate ? referenceDate.toDate() : new Date(referenceDate)) : new Date();
  const currentDate = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  
  let applicableAvailability = null;
  
  // Look for temporary availability first (higher priority)
  const temporaryAvail = availability
    .filter(a => 
      a.employeeId === employeeId && 
      a.type === 'temporary' &&
      a.startDate && a.endDate
    )
    .find(a => {
      const startDate = a.startDate.toDate ? a.startDate.toDate() : new Date(a.startDate);
      const endDate = a.endDate.toDate ? a.endDate.toDate() : new Date(a.endDate);
      return currentDate >= startDate && currentDate <= endDate;
    });
  
  if (temporaryAvail) {
    applicableAvailability = temporaryAvail;
  } else {
    // Fall back to recurring availability
    const recurringAvail = availability.find(a => 
      a.employeeId === employeeId && 
      a.type === 'recurring'
    );
    applicableAvailability = recurringAvail;
  }
  
  if (!applicableAvailability || !applicableAvailability.weeklySchedule) {
    // No availability set - assume available
    return true;
  }

  const dayAvailability = applicableAvailability.weeklySchedule[dayOfWeek];
  if (!dayAvailability) {
    // No availability set for this day - assume available
    return true;
  }

  // Check availability type
  switch (dayAvailability.type) {
    case 'all_day_available':
      return true;
    case 'all_day_not_available':
      return false;
    case 'available':
      // Check if requested time is within available time
      return isTimeInRange(startTime, endTime, dayAvailability.startTime, dayAvailability.endTime);
    case 'not_available':
      // Check if requested time overlaps with the unavailable range
      return !hasTimeOverlap(startTime, endTime, dayAvailability.startTime, dayAvailability.endTime);
    default:
      return true;
  }
}

// Helper function to check if a time range overlaps with another time range
function isTimeInRange(requestStart, requestEnd, availStart, availEnd) {
  const reqStartMin = timeToMinutes(requestStart);
  const reqEndMin = timeToMinutes(requestEnd);
  const availStartMin = timeToMinutes(availStart);
  const availEndMin = timeToMinutes(availEnd);

  // Normalize ranges so that ranges spanning midnight become two segments
  const normalize = (start, end) => {
    if (start <= end) return [[start, end]];
    // spans midnight: split into [start, 1440) and [0, end]
    return [[start, 1440], [0, end]];
  };

  const reqRanges = normalize(reqStartMin, reqEndMin);
  const availRanges = normalize(availStartMin, availEndMin);

  // A request is considered within availability only if every request segment
  // is fully contained within at least one availability segment.
  for (const [rStart, rEnd] of reqRanges) {
    let contained = false;
    for (const [aStart, aEnd] of availRanges) {
      if (rStart >= aStart && rEnd <= aEnd) {
        contained = true;
        break;
      }
    }
    if (!contained) return false;
  }
  return true;
}

// Helper function to check if two time ranges overlap
function hasTimeOverlap(start1, end1, start2, end2) {
  const s1 = timeToMinutes(start1);
  const e1 = timeToMinutes(end1);
  const s2 = timeToMinutes(start2);
  const e2 = timeToMinutes(end2);

  const normalize = (start, end) => {
    if (start <= end) return [[start, end]];
    return [[start, 1440], [0, end]];
  };

  const ranges1 = normalize(s1, e1);
  const ranges2 = normalize(s2, e2);

  // If any pair of segments overlap, the overall ranges overlap
  for (const [aStart, aEnd] of ranges1) {
    for (const [bStart, bEnd] of ranges2) {
      if (aStart < bEnd && bStart < aEnd) return true;
    }
  }
  return false;
}

// Helper function to get employee availability for a specific day
export function getEmployeeAvailabilityForDay(availability, employeeId, dayOfWeek, referenceDate = null) {
  const ref = referenceDate ? (referenceDate.toDate ? referenceDate.toDate() : new Date(referenceDate)) : new Date();
  const currentDate = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  
  // Look for temporary availability first (higher priority)
  const temporaryAvail = availability
    .filter(a => 
      a.employeeId === employeeId && 
      a.type === 'temporary' &&
      a.startDate && a.endDate
    )
    .find(a => {
      const startDate = a.startDate.toDate ? a.startDate.toDate() : new Date(a.startDate);
      const endDate = a.endDate.toDate ? a.endDate.toDate() : new Date(a.endDate);
      return currentDate >= startDate && currentDate <= endDate;
    });
  
  if (temporaryAvail && temporaryAvail.weeklySchedule) {
    return temporaryAvail.weeklySchedule[dayOfWeek];
  }
  
  // Fall back to recurring availability
  const recurringAvail = availability.find(a => 
    a.employeeId === employeeId && 
    a.type === 'recurring'
  );
  
  if (recurringAvail && recurringAvail.weeklySchedule) {
    return recurringAvail.weeklySchedule[dayOfWeek];
  }
  
  return null;
}

// Helper function to convert time string to minutes
function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

// Helper function to get all days an employee is available
export function getEmployeeAvailableDays(availability, employeeId, referenceDate = null) {
  const ref = referenceDate ? (referenceDate.toDate ? referenceDate.toDate() : new Date(referenceDate)) : new Date();
  const currentDate = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  
  let applicableAvailability = null;
  
  // Look for temporary availability first (higher priority)
  const temporaryAvail = availability
    .filter(a => 
      a.employeeId === employeeId && 
      a.type === 'temporary' &&
      a.startDate && a.endDate
    )
    .find(a => {
      const startDate = a.startDate.toDate ? a.startDate.toDate() : new Date(a.startDate);
      const endDate = a.endDate.toDate ? a.endDate.toDate() : new Date(a.endDate);
      return currentDate >= startDate && currentDate <= endDate;
    });
  
  if (temporaryAvail) {
    applicableAvailability = temporaryAvail;
  } else {
    // Fall back to recurring availability
    const recurringAvail = availability.find(a => 
      a.employeeId === employeeId && 
      a.type === 'recurring'
    );
    applicableAvailability = recurringAvail;
  }
  
  if (!applicableAvailability || !applicableAvailability.weeklySchedule) {
    return []; // No availability set
  }
  
  return Object.keys(applicableAvailability.weeklySchedule)
    .filter(dayKey => {
      const dayAvail = applicableAvailability.weeklySchedule[dayKey];
      return dayAvail && (dayAvail.type === 'all_day_available' || dayAvail.type === 'available');
    })
    .map(dayKey => parseInt(dayKey));
}
